import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  GraduationYearsHotResponse,
  MasterGroupedJobTypesResponse,
  RecruitmentListSchema,
  type RecruitmentRankingsResponse,
  RecruitmentRankingsSchema,
  type RecruitmentSchema,
  RecruitmentTagsResponse,
  RecruitmentTypesResponse,
} from '../entities/recruitment/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

/**
 * GetGraduationYearsHot
 */
export type GraduationYearsHotReturnValueType = Result<
  GraduationYearsHotResponseType,
  AppError
>;

export type GraduationYearsHotResponseType = z.infer<
  typeof GraduationYearsHotResponse
>;

type GetGraduationYearsHotArgs = {
  version?: string;
};

type UseGraduationYearsHotOptions = {
  config?: UseQueryOptions<GraduationYearsHotReturnValueType>;
} & GetGraduationYearsHotArgs;

export const getGraduationYearsHot = async ({
  version = DEFAULT_API_VERSION,
}: GetGraduationYearsHotArgs): Promise<GraduationYearsHotReturnValueType> => {
  const path = `/${version}/graduation-years/hot`;
  const result: Result<GraduationYearsHotResponseType, AppError> =
    await defaultClient.get(path, GraduationYearsHotResponse);
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useGraduationYearsHot = ({
  config,
}: UseGraduationYearsHotOptions) => {
  return useQuery<GraduationYearsHotReturnValueType>({
    ...config,
    queryKey: ['GraduationYears'],
    async queryFn() {
      return getGraduationYearsHot({});
    },
  });
};

/**
 * RecruitmentTags
 */
export type RecruitmentTagsReturnValueType = Result<
  RecruitmentTagsResponseType,
  AppError
>;

export type RecruitmentTagsResponseType = z.infer<
  typeof RecruitmentTagsResponse
>;

type GetRecruitmentTagsArgs = {
  version?: string;
};

type UseRecruitmentTagsOptions = {
  config?: UseQueryOptions<RecruitmentTagsReturnValueType>;
} & GetRecruitmentTagsArgs;

export const getRecruitmentTags = async ({
  version = DEFAULT_API_VERSION,
}: GetRecruitmentTagsArgs): Promise<RecruitmentTagsReturnValueType> => {
  const path = `/${version}/recruitment/tags`;
  const result: Result<RecruitmentTagsResponseType, AppError> =
    await defaultClient.get(path, RecruitmentTagsResponse);
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useRecruitmentTags = ({config}: UseRecruitmentTagsOptions) => {
  return useQuery<RecruitmentTagsReturnValueType>({
    ...config,
    queryKey: ['RecruitmentTags'],
    async queryFn() {
      return getRecruitmentTags({});
    },
  });
};

/**
 * GetRecruitmentTypes
 */
export type RecruitmentTypesReturnValueType = Result<
  RecruitmentTypesResponseType,
  AppError
>;

export type RecruitmentTypesResponseType = z.infer<
  typeof RecruitmentTypesResponse
>;

type GetRecruitmentTypesArgs = {
  version?: string;
};

type UseRecruitmentTypesOptions = {
  config?: UseQueryOptions<RecruitmentTypesReturnValueType>;
} & GetRecruitmentTypesArgs;

export const getRecruitmentTypes = async ({
  version = DEFAULT_API_VERSION,
}: GetRecruitmentTypesArgs): Promise<RecruitmentTypesReturnValueType> => {
  const path = `/${version}/recruitment-types`;
  const result: Result<RecruitmentTypesResponseType, AppError> =
    await defaultClient.get(path, RecruitmentTypesResponse);
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useRecruitmentTypes = ({config}: UseRecruitmentTypesOptions) => {
  return useQuery<RecruitmentTypesReturnValueType>({
    ...config,
    queryKey: ['RecruitmentTypes'],
    async queryFn() {
      return getRecruitmentTypes({});
    },
  });
};

/**
 * GetMasterGroupedJobTypes
 */
export type MasterGroupedJobTypesReturnValueType = Result<
  MasterGroupedJobTypesResponseType,
  AppError
>;

export type MasterGroupedJobTypesResponseType = z.infer<
  typeof MasterGroupedJobTypesResponse
>;

type GetMasterGroupedJobTypesArgs = {
  version?: string;
};

type UseMasterGroupedJobTypesOptions = {
  config?: UseQueryOptions<MasterGroupedJobTypesReturnValueType>;
} & GetMasterGroupedJobTypesArgs;

export const getMasterGroupedJobTypes = async ({
  version = DEFAULT_API_VERSION,
}: GetMasterGroupedJobTypesArgs): Promise<MasterGroupedJobTypesReturnValueType> => {
  const path = `/${version}/master/grouped-job-types`;
  const result: Result<MasterGroupedJobTypesResponseType, AppError> =
    await defaultClient.get(path, MasterGroupedJobTypesResponse);
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useMasterGroupedJobTypes = ({
  config,
}: UseMasterGroupedJobTypesOptions) => {
  return useQuery<MasterGroupedJobTypesReturnValueType>({
    ...config,
    queryKey: ['MasterGroupedJobTypes'],
    async queryFn() {
      return getMasterGroupedJobTypes({});
    },
  });
};

/**
 * GetRecruitment
 */

type GetRecruitingInfoListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  queryParams: {
    limit: number;
    page: number;
    order: string;
    industryCategoryIDs?: number[];
    industryIDs?: number[];
    tagIDs?: number[];
    typeIDs?: number[];
    groupedJobTypeIDs?: number[];
    excludeEngineer?: boolean;
    gy?: number;
    allgy?: string;
  };
};
export type Recruitment = z.infer<typeof RecruitmentSchema>;
export type GetRecruitmentListResponse = z.infer<typeof RecruitmentListSchema>;
export type ResultGetRecruitmentList = ResultWithOauthToken<
  GetRecruitmentListResponse,
  AppError
>;

type UseGetCompanyListOptions = {
  config?: UseQueryOptions<ResultGetRecruitmentList>;
} & Omit<GetRecruitingInfoListArgs, 'cookies'>;

function getRecruitmentFromPages(
  data: ResultGetRecruitmentList | undefined,
): GetRecruitmentListResponse | undefined {
  if (!data?.ok) return undefined;
  return data.value;
}

function getRecruitmentRankingsFromResult(
  data: ResultGetRecruitmentRankings | undefined,
): GetRecruitmentRankingsResponse | undefined {
  if (!data?.ok) return undefined;
  return data.value;
}

export const useInfiniteRecruitmentList = ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: UseGetCompanyListOptions): UseInfiniteQueryResult<
  GetRecruitmentListResponse,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['RecruitmentList', queryParams],
    async ({pageParam = {page: 1, cacheID: ''}}) => {
      const cookies = parseCookies();
      const result = await getRecruitmentList({
        cookies,
        version,
        queryParams: {
          ...queryParams,
          page: pageParam.page,
        },
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return getRecruitmentFromPages(result);
    },
    {
      getNextPageParam: (lastPage) => ({
        page: lastPage?.nextPage ?? 0,
      }),
    },
  );
};

export const useInfiniteRecruitmentListForTop = ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: UseGetCompanyListOptions): UseInfiniteQueryResult<
  GetRecruitmentListResponse,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['RecruitmentList', queryParams],
    async ({pageParam = {page: 1, cacheID: ''}}) => {
      const cookies = parseCookies();
      const result = await getRecruitmentList({
        cookies,
        version,
        queryParams: {
          ...queryParams,
          page: pageParam.page,
        },
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) return;

      // 学年不問の募集情報は除外する
      result.value.recruitments = result.value?.recruitments.filter(
        (recruitment) => recruitment.targetYear !== 0,
      );
      result.value.recruitments = result.value?.recruitments.slice(0, 10);
      return getRecruitmentFromPages(result);
    },
    {
      getNextPageParam: (lastPage) => ({
        page: lastPage?.nextPage ?? 0,
      }),
    },
  );
};

export const useRecruitmentList = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetCompanyListOptions): UseQueryResult<
  GetRecruitmentListResponse,
  AppError
> => {
  return useQuery({
    enabled: config?.enabled === undefined ? true : config?.enabled,
    queryKey: ['RecruitmentList', queryParams],
    queryFn: async () => {
      const cookies = parseCookies();
      const data = await getRecruitmentList({
        cookies,
        version,
        queryParams,
      });
      return getRecruitmentFromPages(data);
    },
  });
};

export const getRecruitmentList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetRecruitingInfoListArgs): Promise<ResultGetRecruitmentList> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetRecruitmentListResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment`,
      {...queryParams},
      'none',
    );

    return defaultClient.get(path, RecruitmentListSchema);
  };

  const result = await sendApiRequest<GetRecruitmentListResponse>({
    request,
    cookies,
    authRequired: false,
  });

  return result;
};

/**
 * GetRecruitmentRankings
 */
type GetRecruitmentRankingsArgs = {
  version?: string;
  cookies: Partial<Record<string, string>>;
  queryParams: {
    limit?: number;
  };
};

export type GetRecruitmentRankingsResponse = RecruitmentRankingsResponse;

export type ResultGetRecruitmentRankings = ResultWithOauthToken<
  GetRecruitmentRankingsResponse,
  AppError
>;

export const useGetRecruitmentRankings = ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: Omit<GetRecruitmentRankingsArgs, 'cookies'>): UseQueryResult<
  GetRecruitmentRankingsResponse,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery({
    queryKey: ['getRecruitmentRankings'],
    queryFn: async () => {
      const cookies = parseCookies();
      const result = await getRecruitmentRankings({
        cookies,
        version,
        queryParams,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return getRecruitmentRankingsFromResult(result);
    },
  });
};

export const getRecruitmentRankings = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetRecruitmentRankingsArgs): Promise<ResultGetRecruitmentRankings> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetRecruitmentRankingsResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = createPathWithQuery(
      `/${version}/recruitment/rankings`,
      {...queryParams},
      'none',
    );
    return defaultClient.get(path, RecruitmentRankingsSchema);
  };

  const result = await sendApiRequest<GetRecruitmentRankingsResponse>({
    request,
    cookies,
    authRequired: false,
  });

  return result;
};

type GetFollowingCompanyRecruitmentsArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  queryParams: {
    limit: number;
    page?: number;
  };
  config?: {enabled?: boolean; staleTime?: number};
};

const getFollowingCompanyRecruitments = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetFollowingCompanyRecruitmentsArgs): Promise<ResultGetRecruitmentList> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetRecruitmentListResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = createPathWithQuery(
      `/${version}/users/me/following-company-recruitments`,
      {...queryParams},
      'none',
    );
    return defaultClient.get(path, RecruitmentListSchema);
  };
  const result = await sendApiRequest<GetRecruitmentListResponse>({
    request,
    cookies,
    authRequired: true,
  });

  return result;
};

export const useGetFollowingCompanyRecruitments = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: Omit<GetFollowingCompanyRecruitmentsArgs, 'cookies'>): UseQueryResult<
  GetRecruitmentListResponse,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery({
    enabled: config?.enabled === undefined ? true : config.enabled,
    staleTime: config?.staleTime,
    queryKey: ['getFollowingCompanyRecruitments', queryParams],
    queryFn: async () => {
      const cookies = parseCookies();
      const result = await getFollowingCompanyRecruitments({
        cookies,
        version,
        queryParams,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return getRecruitmentFromPages(result);
    },
  });
};
